import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Maximize2, Eye } from "lucide-react";
import WidgetGridBuilder from "@/components/WidgetGridBuilder";
import { ChefWidgetLiveBody } from "@/components/widget-bodies/ChefWidgetLiveBody";
import CustomWidgetsPanel from "@/components/CustomWidgetsPanel";

/**
 * Widget Builder chef — v3 : grille libre avec drag + resize (react-grid-layout).
 * v3.1 : preview live data pendant l'édition.
 */
export default function ChefDashboardSettings() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [enabledMap, setEnabledMap] = useState({});
  const [widgetsData, setWidgetsData] = useState(null);
  const [preview, setPreview] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: p }, { data: liveData }] = await Promise.all([
          api.get("/chef/widgets/catalog"),
          api.get("/chef/widgets/preferences"),
          api.get("/chef/widgets/data").catch(() => ({ data: null })),
        ]);
        const cat = c.widgets || [];
        setCatalog(cat);
        const initialPrefs = p.preferences || {};
        setPrefs(initialPrefs);
        setWidgetsData(liveData);
        const em = {};
        cat.forEach((w) => { em[w.key] = !!initialPrefs[w.key]?.enabled; });
        setEnabledMap(em);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Reload catalog + data quand un widget custom est ajouté/supprimé
  const reload = async () => {
    try {
      const [{ data: c }, { data: liveData }] = await Promise.all([
        api.get("/chef/widgets/catalog"),
        api.get("/chef/widgets/data").catch(() => ({ data: null })),
      ]);
      const cat = c.widgets || [];
      setCatalog(cat);
      setWidgetsData(liveData);
      setEnabledMap((em) => {
        const next = { ...em };
        cat.forEach((w) => { if (!(w.key in next)) next[w.key] = false; });
        return next;
      });
    } catch (e) { /* silent */ }
  };

  const toggle = (key) => setEnabledMap((m) => ({ ...m, [key]: !m[key] }));

  const save = async () => {
    setSaving(true);
    try {
      // Synchronise enabled dans prefs
      const merged = { ...prefs };
      Object.keys(enabledMap).forEach((k) => {
        merged[k] = { ...(merged[k] || {}), enabled: enabledMap[k], order: merged[k]?.order ?? 0 };
      });
      await api.post("/chef/widgets/preferences", { preferences: merged });
      toast.success("Préférences enregistrées");
      setTimeout(() => navigate("/chef"), 400);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetDefaults = () => {
    const defaults = ["salaries", "planning_today", "conges_pending", "documents", "subscription"];
    const reset = {};
    catalog.forEach((w, i) => {
      reset[w.key] = { enabled: defaults.includes(w.key), order: i, x: null, y: null, w: null, h: null };
    });
    setPrefs(reset);
    setEnabledMap(Object.fromEntries(catalog.map((w) => [w.key, defaults.includes(w.key)])));
    toast.info("Défauts restaurés — cliquez Enregistrer pour valider");
  };

  const enabledCount = Object.values(enabledMap).filter(Boolean).length;

  return (
    <div className="min-h-full" data-testid="chef-dashboard-settings">
      <PageHeader
        eyebrow="Dashboard"
        title="Personnaliser mon tableau de bord"
        sub={`${enabledCount} widget${enabledCount > 1 ? "s" : ""} activé${enabledCount > 1 ? "s" : ""} sur ${catalog.length} · Glissez pour déplacer, tirez le coin pour redimensionner`}
        right={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate("/chef")} data-testid="chef-widgets-back" className="font-semibold">
              <ArrowLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
            <Button onClick={save} disabled={saving} data-testid="chef-widgets-save" className="bg-[#002FA7] text-white">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Sauvegarde…" : "Enregistrer"}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-10">
        {loading ? (
          <div className="text-sm text-[#868E96]">Chargement…</div>
        ) : (
          <>
            <CustomWidgetsPanel onChange={reload} />
            <div className="bg-[#F8F9FA] border border-[#DEE2E6] p-3 mb-4 flex items-center justify-between flex-wrap gap-2" data-testid="chef-widgets-toolbar">
              <div className="text-xs text-[#495057] flex items-center gap-2">
                <Maximize2 className="h-3.5 w-3.5" />
                Grille 12 colonnes — les widgets désactivés sont grisés mais visibles pour edition.
              </div>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer" data-testid="chef-widgets-preview-toggle">
                  <input type="checkbox" checked={preview} onChange={(e) => setPreview(e.target.checked)} className="h-3.5 w-3.5 accent-[#002FA7]" />
                  <Eye className="h-3.5 w-3.5" /> Aperçu live
                </label>
                <button onClick={resetDefaults} data-testid="chef-widgets-reset" className="text-xs underline text-[#868E96] hover:text-[#0A0A0A]">
                  Restaurer les défauts
                </button>
              </div>
            </div>
            <div className="bg-white border border-[#DEE2E6] p-2" data-testid="chef-widgets-grid-editor">
              <WidgetGridBuilder
                catalog={catalog}
                enabledMap={enabledMap}
                prefs={prefs}
                onLayoutChange={setPrefs}
                onToggle={toggle}
                testidPrefix="chef"
                renderBody={preview ? (widget) => <ChefWidgetLiveBody widget={widget} data={widgetsData?.[widget.key]} /> : undefined}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
